import { useState, useEffect, useRef } from "react"
import "../styles/hitterFilters.css"

const RATING_TIERS = [
  { label: "Matchup Rating",         value: "all"       },
  { label: "Elite (≥ .450)",         value: "elite"     },
  { label: "Strong (.400 – .449)",   value: "strong"    },
  { label: "Favorable (.334 – .399)",value: "favorable" },
  { label: "Average (.231 – .333)",  value: "average"   },
  { label: "Poor (≤ .230)",          value: "poor"      },
]

const ARSENAL_TIERS = [
  { label: "Pitch Arsenal Coverage", value: "all" },
  { label: "★★★★★ only",       value: "5"   },
  { label: "★★★★+",            value: "4"   },
  { label: "★★★+",             value: "3"   },
  { label: "★★+",              value: "2"   },
  { label: "★+",               value: "1"   },
]

const HITTER_STAT_FIELDS = [
  { key: "hit_pa",                   label: "Plate Appearances",      hint: "e.g. 300"  },
  { key: "hit_wrc_plus",             label: "wRC+",                   hint: "e.g. 110"  },
  { key: "hit_woba",                 label: "wOBA",                   hint: "e.g. .340", decimal: true },
  { key: "hit_matchup_woba",         label: "Matchup wOBA",           hint: "e.g. .370", decimal: true },
  { key: "hit_iso",                  label: "ISO",                    hint: "e.g. .180", decimal: true },
  { key: "hit_matchup_iso",          label: "Matchup ISO",            hint: "e.g. .200", decimal: true },
  { key: "hit_hard_contact_pct",     label: "Hard Contact %",         hint: "e.g. 40"   },
  { key: "hit_matchup_hard_contact", label: "Matchup Hard Contact %", hint: "e.g. 40"   },
  { key: "hit_hr_per_fb",            label: "HR/FB %",                hint: "e.g. 12"   },
  { key: "hit_matchup_hr_per_fb",    label: "Matchup HR/FB %",        hint: "e.g. 12"   },
  { key: "hit_fb_pct",               label: "FB %",                   hint: "e.g. 38"   },
  { key: "hit_matchup_fb_pct",       label: "Matchup FB %",           hint: "e.g. 38"   },
]

const PITCHER_STAT_FIELDS = [
  { key: "sp_ip",                    label: "IP",                     hint: "e.g. 100"  },
  { key: "sp_woba",                  label: "wOBA",                   hint: "e.g. .320", decimal: true },
  { key: "sp_iso",                   label: "ISO",                    hint: "e.g. .180", decimal: true },
  { key: "sp_hard_contact",          label: "Hard Contact %",         hint: "e.g. 35"   },
  { key: "sp_soft_contact",          label: "Soft Contact %",         hint: "e.g. 18"   },
  { key: "sp_kpct",                  label: "K %",                    hint: "e.g. 22"   },
  { key: "sp_bbpct",                 label: "Walk %",                 hint: "e.g. 8"    },
  { key: "sp_xfip",                  label: "xFIP",                   hint: "e.g. 4.00" },
  { key: "sp_fbpct",                 label: "FB %",                   hint: "e.g. 35"   },
  { key: "sp_gbpct",                 label: "GB %",                   hint: "e.g. 45"   },
  { key: "sp_hr_per_fb",             label: "HR/FB %",                hint: "e.g. 12"   },
]

export const ALL_STAT_FIELDS = [...HITTER_STAT_FIELDS, ...PITCHER_STAT_FIELDS]

function StatInput({ value, hint, decimal, onChange }) {
  const [localValue, setLocalValue] = useState(value)
  const timerRef = useRef(null)

  useEffect(() => { setLocalValue(value) }, [value])
  useEffect(() => () => clearTimeout(timerRef.current), [])

  function handleFocus() {
    if (decimal && localValue === "") setLocalValue("0.")
  }

  function handleBlur() {
    if (decimal && (localValue === "0." || localValue === "0")) {
      setLocalValue("")
      onChange("")
    }
  }

  function handleChange(e) {
    const val = e.target.value
    setLocalValue(val)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => onChange(val), 300)
  }

  return decimal ? (
    <input
      type="text"
      inputMode="decimal"
      className="statInput"
      placeholder={hint}
      value={localValue}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleChange}
    />
  ) : (
    <input
      type="number"
      className="statInput"
      placeholder={hint}
      value={localValue}
      step="any"
      onChange={handleChange}
    />
  )
}

function StatThresholdRow({ fieldKey, label, hint, range, onChange, decimal }) {
  const isActive = range.min !== ""
  return (
    <div className={`statFilterRow ${isActive ? "statFilterRow--active" : ""}`}>
      <span className="statFilterLabel">{label}</span>
      <div className="statFilterInputs">
        <StatInput
          value={range.min}
          hint={hint}
          decimal={decimal}
          onChange={(val) => onChange(fieldKey, val)}
        />
        {isActive && (
          <button
            className="clearStatBtn"
            onClick={() => onChange(fieldKey, "")}
            title="Clear"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  )
}

function StatSection({ title, fields, filters, onChange, isOpen, onToggle }) {
  const activeCount = fields.filter(({ key }) => {
    const r = filters.stats[key]
    return r && r.min !== ""
  }).length

  return (
    <div className="statSection">
      <div className="statSectionHeader" onClick={onToggle}>
        <div className="filtersHeaderLeft">
          <span className="statSectionTitle">{title}</span>
          {activeCount > 0 && (
            <span className="activeFilterBadge">{activeCount} active</span>
          )}
        </div>
        <span className="filtersToggleIcon">{isOpen ? "▲" : "▼"}</span>
      </div>
      {isOpen && (
        <div className="statFiltersGrid">
          {fields.map(({ key, label, hint, decimal }) => (
            <StatThresholdRow
              key={key}
              fieldKey={key}
              label={label}
              hint={hint}
              decimal={decimal}
              range={filters.stats[key] ?? { min: "" }}
              onChange={onChange}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function HitterFilters({ filters, setFilters, teams, activeCount }) {
  const [hitterOpen, setHitterOpen] = useState(false)
  const [pitcherOpen, setPitcherOpen] = useState(false)

  function handleReset() {
    setFilters((prev) => ({
      ...prev,
      ratingTier: "all",
      team: "All",
      arsenalTier: "all",
      stats: Object.fromEntries(
        Object.keys(prev.stats).map((k) => [k, { min: "" }])
      ),
    }))
  }

  function update(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }

  function updateStat(fieldKey, value) {
    setFilters((prev) => ({
      ...prev,
      stats: {
        ...prev.stats,
        [fieldKey]: { min: value },
      },
    }))
  }

  return (
    <div className="filtersWrapper">

      {/* ── Always-visible header bar ── */}
      <div
        className="filtersHeader"
        onClick={() => {
          const anyOpen = hitterOpen || pitcherOpen
          setHitterOpen(!anyOpen)
          setPitcherOpen(!anyOpen)
        }}
      >
        <span className="filtersTitle">Filters</span>

        <div className="filtersHeaderSelects" onClick={(e) => e.stopPropagation()}>
          <select
            className="filterSelectInline"
            value={filters.ratingTier}
            onChange={(e) => update("ratingTier", e.target.value)}
          >
            {RATING_TIERS.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>

          <select
            className="filterSelectInline"
            value={filters.team}
            onChange={(e) => update("team", e.target.value)}
          >
            <option value="All">All Teams</option>
            {teams.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>

          <select
            className="filterSelectInline"
            value={filters.arsenalTier}
            onChange={(e) => update("arsenalTier", e.target.value)}
          >
            {ARSENAL_TIERS.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <div className="filtersHeaderRight">
          {activeCount > 0 && (
            <span className="activeFilterBadge">{activeCount} active</span>
          )}
          {activeCount > 0 && (
            <button
              className="resetBtn"
              onClick={handleReset}
            >
              Reset
            </button>
          )}
          <span className="filtersToggleIcon">{(hitterOpen || pitcherOpen) ? "▲" : "▼"}</span>
        </div>
      </div>

      {/* ── Expandable stat sections ── */}
      {(hitterOpen || pitcherOpen) && (
        <div className="filtersBody">
          <StatSection
            title="Hitter Stats"
            fields={HITTER_STAT_FIELDS}
            filters={filters}
            onChange={updateStat}
            isOpen={hitterOpen}
            onToggle={() => setHitterOpen((p) => !p)}
          />
          <StatSection
            title="Pitcher Stats"
            fields={PITCHER_STAT_FIELDS}
            filters={filters}
            onChange={updateStat}
            isOpen={pitcherOpen}
            onToggle={() => setPitcherOpen((p) => !p)}
          />
        </div>
      )}

    </div>
  )
}

export default HitterFilters